/**
 * Safety helpers for retrying a request through the same-origin proxy.
 * Two distinct concerns, both critical when switching the URL of a request
 * from cross-origin → same-origin:
 *
 *   1. HTTP method safety — see `isSafeHttpMethod`.
 *   2. Credential mode    — see `proxyRetryCredentials`.
 */

// RFC 7231 §4.2.1 — "safe" methods are read-only / no side effects.
//
// NOTE: `fetch-retry` does NOT use this set. With its default config it retries
// ANY rejected promise (network error) regardless of HTTP method. The
// method-awareness lives in OUR code: `useFetchBase` passes `retries: 0` for
// unsafe methods (see SAFE_METHOD_* there), and the catch-side proxy fallback
// below is gated on `isSafeHttpMethod`. Do not assume the library guards
// unsafe methods on your behalf.
const SAFE_HTTP_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * Whether retrying a request with this method is side-effect-safe.
 *
 * Why this matters for the catch-side fallback in `useFetchBase`:
 * when the direct cross-origin `fetch()` throws (TypeError / CORS / network),
 * the browser DOES NOT tell us whether the request actually reached the
 * backend. For unsafe methods (POST/PUT/PATCH/DELETE) the backend may have
 * already executed the side effect (charge, email, mutation) — a retry would
 * duplicate it. So we only retry on safe methods.
 *
 * The in-try interstitial retry (200/401/403/429 + HTML) is exempt: those
 * responses are generated at the Vercel edge BEFORE the backend sees the
 * request, so retrying is guaranteed not to duplicate side effects.
 */
export function isSafeHttpMethod(method: string | undefined): boolean {
    return SAFE_HTTP_METHODS.has((method ?? "GET").toUpperCase());
}

/**
 * Pick the right `credentials` mode for a proxy retry.
 *
 * Why this matters: the original direct call was CROSS-ORIGIN. The default
 * `RequestCredentials` is `"same-origin"`, which means "send no cookies for
 * cross-origin calls". When we switch the URL to `/api/v1/*`, that same
 * `"same-origin"` mode now means "DO send first-party cookies" — silently
 * forwarding `innerbalance.com` cookies (auth session, analytics, etc.) to
 * `BACKEND_API_ORIGIN` via the Vercel proxy. That's a privacy leak and a
 * behaviour change under the caller's feet.
 *
 * Rule:
 *   • Caller explicitly set `"include"` or `"omit"` → respect it.
 *   • Otherwise (unset OR `"same-origin"`) → force `"omit"` to mirror the
 *     original cross-origin no-cookie semantics.
 */
export function proxyRetryCredentials(
    init: RequestInit | undefined,
): RequestCredentials {
    const explicit = init?.credentials;
    if (explicit === "include" || explicit === "omit") return explicit;
    return "omit";
}
